import { useState, useRef, useCallback } from 'react'

interface Position {
  x: number
  y: number
}

interface DragConstraints {
  minX?: number
  minY?: number
  maxX?: number
  maxY?: number
}

export default function useDraggable(defaultPos: Position | null = null, constraints: DragConstraints = {}) {
  const [pos, setPos] = useState<Position | null>(defaultPos)
  const dragging = useRef(false)
  const wasDrag  = useRef(false)
  const start    = useRef({ x: 0, y: 0 })
  const offset   = useRef({ x: 0, y: 0 })
  const THRESHOLD = 5

  const clamp = useCallback((x: number, y: number): Position => {
    const cx = Math.max(constraints.minX ?? 0, Math.min(x, constraints.maxX ?? window.innerWidth - 40))
    const cy = Math.max(constraints.minY ?? 0, Math.min(y, constraints.maxY ?? window.innerHeight - 40))
    return { x: cx, y: cy }
  }, [constraints.minX, constraints.minY, constraints.maxX, constraints.maxY])

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    // Só arrastar com botão principal / toque
    if (e.button && e.button !== 0) return
    e.preventDefault()

    const rect = e.currentTarget.getBoundingClientRect()
    start.current = { x: e.clientX, y: e.clientY }
    offset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top }
    dragging.current = true
    wasDrag.current = false

    function onMove(ev: PointerEvent) {
      const dx = ev.clientX - start.current.x
      const dy = ev.clientY - start.current.y
      if (!wasDrag.current && Math.abs(dx) < THRESHOLD && Math.abs(dy) < THRESHOLD) return
      wasDrag.current = true
      const newX = ev.clientX - offset.current.x
      const newY = ev.clientY - offset.current.y
      setPos(clamp(newX, newY))
    }

    function onUp() {
      dragging.current = false
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }, [clamp])

  return { pos, setPos, onPointerDown, dragging: dragging.current, wasDrag }
}
